const REGION_MAPPING: Record<string, string> = {
  EUW: "euw",
  EUW1: "euw",
  EUNE: "eune",
  EUNE1: "eune",
  EUN1: "eune",
  NA: "na",
  NA1: "na",
  BR: "br",
  BR1: "br",
  LAN: "lan",
  LA1: "lan",
  LAS: "las",
  LA2: "las",
  OCE: "oce",
  OC1: "oce",
  RU: "ru",
  RU1: "ru",
  TR: "tr",
  TR1: "tr",
  ME1: "me",
  JP: "jp",
  JP1: "jp",
  KR: "kr",
  KR1: "kr",
  SG2: "sg",
  TW2: "tw",
  VN2: "vn",
};

/** Riot-регион (EUW1/RU/…) → код региона op.gg (euw/ru/…). null если пусто. */
export function toOpggRegion(region?: string | null): string | null {
  const normalized = String(region ?? "").trim().toUpperCase();
  if (!normalized) return null;
  return REGION_MAPPING[normalized] || normalized.toLowerCase().replace(/1$/, "");
}

export function getOpggUrl(
  riotGameName?: string,
  riotTagLine?: string,
  riotRegion?: string | null,
) {
  if (!riotGameName || !riotTagLine || !riotRegion) {
    return null;
  }
  const opggRegion = toOpggRegion(riotRegion);
  if (!opggRegion) return null;

  const encodedName = encodeURIComponent(riotGameName);
  const encodedTag = encodeURIComponent(riotTagLine);

  return `https://op.gg/ru/lol/summoners/${opggRegion}/${encodedName}-${encodedTag}`;
}

/**
 * Мультисёрч op.gg. Формат: `/multisearch/{region}?summoners=Name%23TAG,Name2%23TAG2`
 * (имена через запятую, `#` сохраняется). region — уже код op.gg (euw/ru/…).
 */
export function getOpggMultiSearchUrl(
  region: string,
  namesWithTags: Array<string>,
) {
  const summoners = namesWithTags
    .map((n) => n.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join(",");
  return `https://op.gg/ru/lol/multisearch/${region}?summoners=${encodeURIComponent(summoners)}`;
}

export interface OpggAccount {
  gameName?: string | null;
  tagLine?: string | null;
  region?: string | null;
  isPrimary?: boolean | null;
}

export interface OpggMember {
  gameName?: string | null;
  tagLine?: string | null;
  region?: string | null;
  /** Все аккаунты игрока — если есть, используем для подбора alt на дом-регионе. */
  accounts?: OpggAccount[] | null;
}

export interface TeamMultiSearch {
  /** Код региона op.gg, по которому собран мультисёрч. */
  region: string;
  url: string;
  /** Сколько игроков попало в мультисёрч (на этом регионе). */
  included: number;
  /** Всего игроков с Riot-аккаунтом. */
  total: number;
}

/**
 * Мультисёрч команды с фильтрацией по региону: op.gg НЕ умеет смешивать регионы,
 * поэтому выбираем доминирующий регион (где больше всего аккаунтов) и включаем
 * только игроков на нём. Остальные (другой регион) в этот мультисёрч не попадают.
 * Возвращает null, если на доминирующем регионе меньше 2 игроков.
 */
export function getTeamOpggMultiSearch(
  members: Array<OpggMember>,
): TeamMultiSearch | null {
  // Нормализуем: каждый игрок → список его валидных аккаунтов на op.gg-регионах.
  // Если массива accounts нет — берём единственный (основной) из плоских полей.
  const normalized = members
    .map((m) => {
      const accs =
        m.accounts && m.accounts.length > 0
          ? m.accounts
          : [{ gameName: m.gameName, tagLine: m.tagLine, region: m.region, isPrimary: true }];
      return accs
        .map((a) => ({
          gameName: a.gameName,
          tagLine: a.tagLine,
          opggRegion: a.gameName && a.tagLine ? toOpggRegion(a.region) : null,
          isPrimary: Boolean(a.isPrimary),
        }))
        .filter((a) => a.opggRegion);
    })
    .filter((list) => list.length > 0);

  if (normalized.length < 2) return null;

  // Доминирующий регион — по ОСНОВНЫМ аккаунтам (где больше всего primary).
  const primaryCount: Record<string, number> = {};
  for (const list of normalized) {
    const primary = list.find((a) => a.isPrimary) ?? list[0]!;
    const r = primary.opggRegion!;
    primaryCount[r] = (primaryCount[r] ?? 0) + 1;
  }
  const dominant = Object.entries(primaryCount).sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  )[0]?.[0];
  if (!dominant) return null;

  // У каждого игрока берём аккаунт на дом-регионе: основной если совпал, иначе
  // вторичный на этом регионе. Нет аккаунта на дом-регионе → игрок не попадает.
  const names: string[] = [];
  for (const list of normalized) {
    const acc =
      list.find((a) => a.isPrimary && a.opggRegion === dominant) ??
      list.find((a) => a.opggRegion === dominant);
    if (acc) names.push(`${acc.gameName}#${acc.tagLine}`);
  }
  if (names.length < 2) return null;

  return {
    region: dominant,
    url: getOpggMultiSearchUrl(dominant, names),
    included: names.length,
    total: normalized.length,
  };
}
